import { License, LicenseSeat } from "../../store/types";
import { ColumnMeta, CustomAction } from "../CustomDatatable/CustomDatatable";
import {
  formatPeriod,
  formatDate,
  formatDateTime,
} from "../../hooks/dateTimeConvertion";
import { isSessionLicense } from "../../hooks/useLicenseType";
import StatusTag from "../StatusTag/StatusTag";
import { MenuItem } from "primereact/menuitem";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import SessionMembersList from "../SessionMembersList/SessionMembersList";
import { onlineTemplate } from "./UserTableTemplates";
import {
  OnlineTag,
  OfflineTag,
  FullTag,
} from "../../styles/PageLayoutStyles.styles";
import {
  filterTextInput,
  textFilterTemplate,
  dateFilterTemplate,
  dateFilterFunction,
  projectFilterTemplate,
  companyFilterTemplate,
  filterEqualsOption,
} from "./FilterTemplates";
import {
  IconCircleCheck,
  IconCircleX,
  IconAddUser,
  IconEditUser,
  IconDeleteUser,
} from "../../assets/icons/Icon";

const DAY_MS = 1000 * 60 * 60 * 24;

/** Количество занятых мест: seatCount приходит в списке, seats — в карточке */
export const getSeatCount = (license: License | null | undefined): number => {
  if (!license) return 0;
  if (typeof license.seatCount === "number") return license.seatCount;
  return license.seats?.length ?? 0;
};

/** Лицензия ещё не истекла (у бессрочной expiresAt === null) */
export const isNotExpired = (license: License): boolean => {
  if (!license.expiresAt) return true;
  const end = new Date(license.expiresAt);
  if (isNaN(end.getTime())) return true;
  return end.getTime() > Date.now();
};

/** Срок действия в днях, Infinity — бессрочная лицензия */
export const getValidityDays = (license: License): number => {
  if (!license.expiresAt) return Infinity;
  if (!license.startsAt) return 0;
  const start = new Date(license.startsAt).getTime();
  const end = new Date(license.expiresAt).getTime();
  if (isNaN(start) || isNaN(end)) return 0;
  return Math.max(0, (end - start) / DAY_MS);
};

/** Оставшееся время в днях, Infinity — бессрочная лицензия */
export const getRemainingDays = (license: License): number => {
  if (!license.expiresAt) return Infinity;
  const end = new Date(license.expiresAt).getTime();
  if (isNaN(end)) return Infinity;
  return (end - Date.now()) / DAY_MS;
};

export const getLicenseContextMenuItems = (
  selectedLicense: License | null,
  handleAddUser: (license: License) => void,
  handleEditUser: (license: License) => void,
  handleDeleteUser: (license: License) => void,
  handleEnable: (license: License) => void,
  handleDisable: (license: License) => void
): MenuItem[] => {
  if (!selectedLicense) return [];

  const items: MenuItem[] = [];

  // Сессионные лицензии содержат несколько мест — ими управляют на странице лицензии
  if (!isSessionLicense(selectedLicense.kind)) {
    if (getSeatCount(selectedLicense) > 0) {
      items.push({
        label: "Редактировать пользователя",
        icon: <IconEditUser size={20} />,
        command: () => handleEditUser(selectedLicense),
      });
      items.push({
        label: "Удалить пользователя",
        icon: <IconDeleteUser size={20} />,
        command: () => handleDeleteUser(selectedLicense),
      });
    } else {
      items.push({
        label: "Добавить пользователя",
        icon: <IconAddUser size={20} />,
        command: () => handleAddUser(selectedLicense),
      });
    }
  }

  if (selectedLicense.isEnabled) {
    items.push({
      label: "Деактивировать",
      icon: <IconCircleX size={14} />,
      command: () => handleDisable(selectedLicense),
    });
  } else if (isNotExpired(selectedLicense)) {
    items.push({
      label: "Активировать",
      icon: <IconCircleCheck size={14} />,
      command: () => handleEnable(selectedLicense),
    });
  }

  return items;
};

export const getLicenseMobileActions = (
  handleEnable: (license: License) => void,
  handleDisable: (license: License) => void
): CustomAction<License>[] => {
  return [
    {
      icon: <IconCircleCheck size={16} />,
      onClick: (license: License) => handleEnable(license),
      condition: (license: License) =>
        !license.isEnabled && isNotExpired(license),
    },
    {
      icon: <IconCircleX size={16} />,
      onClick: (license: License) => handleDisable(license),
      condition: (license: License) => license.isEnabled,
    },
  ];
};

export const sessionLicenseRowExpansionTemplate = (rowData: any) => {
  if (!isSessionLicense(rowData?.kind)) return null;
  return <SessionMembersList />;
};

export const isActivatedBodyTemplate = (rowData: License) => {
  const status = rowData.isEnabled ? "success" : "danger";
  const label = rowData.isEnabled ? "Активирована" : "Деактивирована";
  return <StatusTag status={status} label={label} />;
};

export const isActiveBodyTemplate = (rowData: License) => {
  const status = rowData.isActive ? "success" : "danger";
  const label = rowData.isActive ? "Действует" : "Не действует";
  return <StatusTag status={status} label={label} />;
};

/**
 * Колонка «Онлайн»: у сессионных лицензий — сейчас онлайн / максимум мест,
 * у остальных — статус единственного пользователя.
 */
export const licenseOnlineTemplate = (rowData: License) => {
  if (!isSessionLicense(rowData.kind))
    return onlineTemplate({ isOnline: (rowData.onlineCount ?? 0) > 0 });

  const online = rowData.onlineCount ?? 0;
  const max = rowData.maxActiveUsers;

  // серый — никого, синий — все места заняты, зелёный — где-то посередине
  let Dot = OnlineTag;
  if (online <= 0) Dot = OfflineTag;
  else if (max && online >= max) Dot = FullTag;

  const count = typeof rowData.onlineCount === "number" ? String(online) : "—";

  return (
    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
      <Dot />
      <p style={{ margin: 0 }}>{max ? `${count} / ${max}` : count}</p>
    </div>
  );
};

export const validityPeriodTemplate = (rowData: License) => {
  if (!rowData.expiresAt) return "Бессрочно";
  if (!rowData.startsAt) return "—";
  const start = new Date(rowData.startsAt);
  const end = new Date(rowData.expiresAt);
  return formatPeriod(end.getTime() - start.getTime());
};

export const timeRemainingTemplate = (rowData: License) => {
  if (!rowData?.expiresAt)
    return <StatusTag status="success" label="Бессрочно" />;

  const expiration = new Date(rowData.expiresAt);
  const now = new Date();

  const endDate = new Date(
    expiration.getFullYear(),
    expiration.getMonth(),
    expiration.getDate()
  );
  const currentDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  const diffMs = endDate.getTime() - currentDate.getTime();

  if (diffMs < 0) {
    return <StatusTag status="danger" label="Истекло" />;
  }

  const daysRemaining = diffMs / DAY_MS;
  const status = daysRemaining >= 30 ? "success" : "warning";

  return <StatusTag status={status} label={formatPeriod(diffMs)} />;
};

export const dateTemplate = (field: keyof License) => (rowData: License) => {
  const value = rowData[field] as string;
  return value ? formatDate(value) : "—";
};

const kindFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <MultiSelect
      value={options.value}
      options={[
        { label: "Временная", value: "Временная" },
        { label: "Бессрочная", value: "Бессрочная" },
        { label: "Сессионная", value: "Сессионная" },
      ]}
      onChange={(e: MultiSelectChangeEvent) => options.filterApplyCallback(e.value)}
      optionLabel="label"
      placeholder="Тип"
      className="p-column-filter"
    />
  );
};

const isEnabledFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <MultiSelect
      value={options.value}
      options={[
        { label: "Активирована", value: true },
        { label: "Деактивирована", value: false },
      ]}
      onChange={(e: MultiSelectChangeEvent) => options.filterApplyCallback(e.value)}
      optionLabel="label"
      placeholder="Любая"
      className="p-column-filter"
    />
  );
};

const isActiveFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <MultiSelect
      value={options.value}
      options={[
        { label: "Действует", value: true },
        { label: "Не действует", value: false },
      ]}
      onChange={(e: MultiSelectChangeEvent) => options.filterApplyCallback(e.value)}
      optionLabel="label"
      placeholder="Любая"
      className="p-column-filter"
    />
  );
};

export type ValidityPeriodFilter =
  | null
  | "less2weeks"
  | "less1month"
  | "less6months"
  | "less1year"
  | "1to3years"
  | "more3years";

export type TimeRemainingFilter =
  | null
  | "expired"
  | "less30days"
  | "more30days"
  | "less1year"
  | "more1year";

export const validityPeriodOptions = [
  { label: "Любой", value: null },
  { label: "Бессрочные", value: "perpetual" },
  { label: "До 2 недель", value: "less2weeks" },
  { label: "До 1 месяца", value: "less1month" },
  { label: "До 6 месяцев", value: "less6months" },
  { label: "До 1 года", value: "less1year" },
  { label: "1–3 года", value: "1to3years" },
  { label: "Более 3 лет", value: "more3years" },
];

export const timeRemainingOptions = [
  { label: "Любое", value: null },
  { label: "Бессрочные", value: "perpetual" },
  { label: "Истекло", value: "expired" },
  { label: "Менее 30 дней", value: "less30days" },
  { label: "30 дней и более", value: "more30days" },
  { label: "До 1 года", value: "less1year" },
  { label: "Более 1 года", value: "more1year" },
];

const validityPeriodFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <Dropdown
      value={options.value}
      options={validityPeriodOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Срок действия"
      className="p-column-filter"
    />
  );
};

const timeRemainingFilterTemplate = (
  options: ColumnFilterElementTemplateOptions
) => {
  return (
    <Dropdown
      value={options.value}
      options={timeRemainingOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="Оставшееся время"
      className="p-column-filter"
    />
  );
};

export const filterByValidityPeriod = (value: number, filter: string) => {
  if (!filter) return true;
  const days = value ?? 0;
  if (filter === "perpetual") return !isFinite(days);
  switch (filter) {
    case "less2weeks":
      return days < 14;
    case "less1month":
      return days < 30;
    case "less6months":
      return days < 180;
    case "less1year":
      return days < 365;
    case "1to3years":
      return days >= 365 && days <= 1095;
    case "more3years":
      return days > 1095;
    default:
      return true;
  }
};

export const filterByTimeRemaining = (value: number, filter: string | null) => {
  if (!filter) return true;
  const days = value ?? 0;
  if (filter === "perpetual") return !isFinite(days);
  switch (filter) {
    case "expired":
      return days < 0;
    case "less30days":
      return days >= 0 && days < 30;
    case "more30days":
      return days >= 30;
    case "less1year":
      return days >= 0 && days < 365;
    case "more1year":
      return days >= 365;
    default:
      return true;
  }
};

const filterLicenseKind = (value: string, filter: string[]): boolean => {
  if (!filter || filter.length === 0) return true;
  return filter.includes(value);
};

const filterBoolean = (value: boolean, filter: boolean[]): boolean => {
  if (!filter || filter.length === 0) return true;
  return filter.includes(value);
};

const projectName = {
  field: "projectName",
  header: "Программное обеспечение",
  filter: true,
  filterField: "projectName",
  filterElement: projectFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterEqualsOption,
  showFilterMatchModes: false,
};

const kindLabel = {
  field: "kindLabel",
  header: "Тип",
  filter: true,
  filterField: "kindLabel",
  filterElement: kindFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterLicenseKind,
  showFilterMatchModes: false,
};

const isEnabled = {
  field: "isEnabled",
  header: "Активирована",
  body: isActivatedBodyTemplate,
  filter: true,
  filterField: "isEnabled",
  filterElement: isEnabledFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterBoolean,
  showFilterMatchModes: false,
};

const isActive = {
  field: "isActive",
  header: "Статус",
  body: isActiveBodyTemplate,
  filter: true,
  filterField: "isActive",
  filterElement: isActiveFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterBoolean,
  showFilterMatchModes: false,
};

const validityPeriod = {
  field: "_validityDays",
  header: "Срок действия",
  body: validityPeriodTemplate,
  filter: true,
  filterField: "_validityDays",
  filterElement: validityPeriodFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterByValidityPeriod,
  showFilterMatchModes: false,
};

const issuedAt = {
  field: "issuedAt",
  header: "Дата создания",
  body: dateTemplate("issuedAt"),
  dataType: "date",
  filter: true,
  filterField: "issuedAt",
  filterElement: dateFilterTemplate,
  filterFunction: dateFilterFunction,
  filterMatchMode: "custom",
  showFilterMatchModes: false,
};

const startsAt = {
  field: "startsAt",
  header: "Дата начала",
  body: dateTemplate("startsAt"),
  dataType: "date",
  filter: true,
  filterField: "startsAt",
  filterElement: dateFilterTemplate,
  filterFunction: dateFilterFunction,
  filterMatchMode: "custom",
  showFilterMatchModes: false,
};

const expiresAt = {
  field: "expiresAt",
  header: "Дата завершения",
  body: dateTemplate("expiresAt"),
  dataType: "date",
  filter: true,
  filterField: "expiresAt",
  filterElement: dateFilterTemplate,
  filterFunction: dateFilterFunction,
  filterMatchMode: "custom",
  showFilterMatchModes: false,
};

const timeRemainingDays = {
  field: "_timeRemainingDays",
  header: "Оставшееся время",
  body: timeRemainingTemplate,
  filter: true,
  filterField: "_timeRemainingDays",
  filterElement: timeRemainingFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterByTimeRemaining,
  showFilterMatchModes: false,
};

const licenseOnline = {
  field: "onlineCount",
  header: "Онлайн",
  body: licenseOnlineTemplate,
  dataType: "numeric",
  sortable: true,
};

const organizationName = {
  field: "organizationName",
  header: "Компания",
  filter: true,
  filterField: "organizationName",
  filterElement: companyFilterTemplate,
  filterMatchMode: "custom",
  filterFunction: filterEqualsOption,
  showFilterMatchModes: false,
};

export const userLicenseColumns: ColumnMeta<License>[] = [
  projectName,
  kindLabel,
  isEnabled,
  validityPeriod,
  startsAt,
  expiresAt,
  timeRemainingDays,
];

export const companyLicenseColumns: ColumnMeta<License>[] = [
  projectName,
  kindLabel,
  isEnabled,
  licenseOnline,
  validityPeriod,
  startsAt,
  expiresAt,
  timeRemainingDays,
];

export const projectLicenseColumns: ColumnMeta<License>[] = [
  organizationName,
  kindLabel,
  isEnabled,
  licenseOnline,
  validityPeriod,
  startsAt,
  expiresAt,
  timeRemainingDays,
];

export const extendedLicenseColumns: ColumnMeta<License>[] = [
  projectName,
  organizationName,
  kindLabel,
  isEnabled,
  licenseOnline,
  startsAt,
  expiresAt,
  validityPeriod,
  timeRemainingDays,
];

/* ------------------------------ Места лицензии ----------------------------- */

const seatDateTimeTemplate = (field: keyof LicenseSeat) => (seat: any) => {
  const value = seat?.[field];
  return value ? formatDateTime(String(value)) : "—";
};

export const seatActivatedTemplate = (seat: any) => (
  <StatusTag
    status={seat?.isActivated ? "success" : "danger"}
    label={seat?.isActivated ? "Активирован" : "Не активирован"}
  />
);

export const licenseSeatColumns: ColumnMeta<any>[] = [
  {
    field: "fullName",
    header: "ФИО",
    filter: true,
    filterField: "fullName",
    filterElement: textFilterTemplate,
    filterFunction: filterTextInput,
    showFilterMatchModes: true,
  },
  {
    field: "isActivated",
    header: "Активирован",
    body: seatActivatedTemplate,
  },
  {
    field: "isOnline",
    header: "Онлайн",
    body: onlineTemplate,
  },
  {
    field: "firstUsedAt",
    header: "Первое использование",
    body: seatDateTimeTemplate("firstUsedAt"),
  },
  {
    field: "lastHandshakeAt",
    header: "Последняя активность",
    body: seatDateTimeTemplate("lastHandshakeAt"),
  },
];
